import React, { useMemo } from 'react';
import { Segment, Header, Icon, Form, Button, Dropdown, Popup } from 'semantic-ui-react';
import { EContractStatus } from '../../services/contracts/ContractService';
import { getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';
import { getContractStatusFilterDropdownOptions } from '../../utils/contractStatusUtils';
import { getContractListClusterFilterOptions } from '../../utils/contractClusterTokens';

export type OperationalFilterTriState = '' | 'yes' | 'no';

export interface ContractFiltersProps {
  clientSearchFilter: string;
  setClientSearchFilter: (v: string) => void;
  tipoContratacaoFilter: string;
  setTipoContratacaoFilter: (v: string) => void;
  valorMinimoFilter: string;
  setValorMinimoFilter: (v: string) => void;
  valorMaximoFilter: string;
  setValorMaximoFilter: (v: string) => void;
  dataInicioFilter: string;
  setDataInicioFilter: (v: string) => void;
  dataFimFilter: string;
  setDataFimFilter: (v: string) => void;
  statusFilter: EContractStatus | '';
  setStatusFilter: (v: EContractStatus | '') => void;
  temTrocaLinhaTriFilter: OperationalFilterTriState;
  setTemTrocaLinhaTriFilter: (v: OperationalFilterTriState) => void;
  temPendenciaSuspensaoTriFilter: OperationalFilterTriState;
  setTemPendenciaSuspensaoTriFilter: (v: OperationalFilterTriState) => void;
  temAparelhoCatalogoTriFilter: OperationalFilterTriState;
  setTemAparelhoCatalogoTriFilter: (v: OperationalFilterTriState) => void;
  effectiveClustersFilter: string[];
  setEffectiveClustersFilter: (v: string[]) => void;
  userNameFilter: string;
  setUserNameFilter: (v: string) => void;
  enderecoCidadeFilter: string;
  setEnderecoCidadeFilter: (v: string) => void;
  enderecoEstadoFilter: string;
  setEnderecoEstadoFilter: (v: string) => void;
  administratorNomeFilter: string;
  setAdministratorNomeFilter: (v: string) => void;
  searchTermFilter: string;
  setSearchTermFilter: (v: string) => void;
  showAdvancedFilters: boolean;
  setShowAdvancedFilters: (v: boolean) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  contractsLoading: boolean;
  canViewAdvancedFilters?: boolean;
}

const ESTADOS_OPTIONS = [
  { key: 'todos', text: 'Todos os estados', value: '' },
  { key: 'AC', text: 'Acre', value: 'AC' },
  { key: 'AL', text: 'Alagoas', value: 'AL' },
  { key: 'AP', text: 'Amapá', value: 'AP' },
  { key: 'AM', text: 'Amazonas', value: 'AM' },
  { key: 'BA', text: 'Bahia', value: 'BA' },
  { key: 'CE', text: 'Ceará', value: 'CE' },
  { key: 'DF', text: 'Distrito Federal', value: 'DF' },
  { key: 'ES', text: 'Espírito Santo', value: 'ES' },
  { key: 'GO', text: 'Goiás', value: 'GO' },
  { key: 'MA', text: 'Maranhão', value: 'MA' },
  { key: 'MT', text: 'Mato Grosso', value: 'MT' },
  { key: 'MS', text: 'Mato Grosso do Sul', value: 'MS' },
  { key: 'MG', text: 'Minas Gerais', value: 'MG' },
  { key: 'PA', text: 'Pará', value: 'PA' },
  { key: 'PB', text: 'Paraíba', value: 'PB' },
  { key: 'PR', text: 'Paraná', value: 'PR' },
  { key: 'PE', text: 'Pernambuco', value: 'PE' },
  { key: 'PI', text: 'Piauí', value: 'PI' },
  { key: 'RJ', text: 'Rio de Janeiro', value: 'RJ' },
  { key: 'RN', text: 'Rio Grande do Norte', value: 'RN' },
  { key: 'RS', text: 'Rio Grande do Sul', value: 'RS' },
  { key: 'RO', text: 'Rondônia', value: 'RO' },
  { key: 'RR', text: 'Roraima', value: 'RR' },
  { key: 'SC', text: 'Santa Catarina', value: 'SC' },
  { key: 'SP', text: 'São Paulo', value: 'SP' },
  { key: 'SE', text: 'Sergipe', value: 'SE' },
  { key: 'TO', text: 'Tocantins', value: 'TO' },
];

const TRI_STATE_OPTIONS = [
  { key: 'all', text: 'Todos', value: '' },
  { key: 'yes', text: 'Sim', value: 'yes' },
  { key: 'no', text: 'Não', value: 'no' },
];

const ContractFilters: React.FC<ContractFiltersProps> = ({
  clientSearchFilter,
  setClientSearchFilter,
  tipoContratacaoFilter,
  setTipoContratacaoFilter,
  valorMinimoFilter,
  setValorMinimoFilter,
  valorMaximoFilter,
  setValorMaximoFilter,
  dataInicioFilter,
  setDataInicioFilter,
  dataFimFilter,
  setDataFimFilter,
  statusFilter,
  setStatusFilter,
  temTrocaLinhaTriFilter,
  setTemTrocaLinhaTriFilter,
  temPendenciaSuspensaoTriFilter,
  setTemPendenciaSuspensaoTriFilter,
  temAparelhoCatalogoTriFilter,
  setTemAparelhoCatalogoTriFilter,
  effectiveClustersFilter,
  setEffectiveClustersFilter,
  userNameFilter,
  setUserNameFilter,
  enderecoCidadeFilter,
  setEnderecoCidadeFilter,
  enderecoEstadoFilter,
  setEnderecoEstadoFilter,
  administratorNomeFilter,
  setAdministratorNomeFilter,
  searchTermFilter,
  setSearchTermFilter,
  showAdvancedFilters,
  setShowAdvancedFilters,
  applyFilters,
  clearFilters,
  contractsLoading,
  canViewAdvancedFilters = false,
}) => {
  const statusDropdownOptions = useMemo(() => getContractStatusFilterDropdownOptions(), []);
  const clusterFilterOptions = useMemo(() => getContractListClusterFilterOptions(), []);

  return (
    <Segment style={{ marginBottom: '20px' }}>
      <Header as="h4">
        <Icon name="filter" />
        Filtros
      </Header>

      <Form>
        <Form.Group widths="equal" stackable>
          <Form.Input
            fluid
            label="Buscar por cliente"
            placeholder="Buscar por razão social, nome fantasia ou CNPJ"
            icon="search"
            value={clientSearchFilter}
            onChange={(e) => setClientSearchFilter(e.target.value)}
          />
          <Form.Select
            fluid
            label="Tipo de contratação"
            placeholder="Todos os tipos"
            options={[
              { key: 'todos', text: 'Todos', value: '' },
              { key: 'aditivo', text: 'Aditivo', value: 'Aditivo' },
              { key: 'renegociacao', text: 'Renegociação', value: 'Renegociação' },
            ]}
            value={tipoContratacaoFilter}
            onChange={(e, { value }) => setTipoContratacaoFilter((value as string) ?? '')}
          />
          <Form.Input
            fluid
            label="Valor mínimo (R$)"
            placeholder="0,00"
            type="number"
            step="0.01"
            min="0"
            value={valorMinimoFilter}
            onChange={(e) => setValorMinimoFilter(e.target.value)}
          />
          <Form.Input
            fluid
            label="Valor máximo (R$)"
            placeholder="0,00"
            type="number"
            step="0.01"
            min="0"
            value={valorMaximoFilter}
            onChange={(e) => setValorMaximoFilter(e.target.value)}
          />
        </Form.Group>
        <Form.Group widths="equal" stackable>
          <Form.Field>
            <label>Status do Contrato</label>
            <Dropdown
              placeholder="Todos os status"
              fluid
              selection
              search
              clearable
              options={statusDropdownOptions}
              value={statusFilter === '' ? '' : String(statusFilter)}
              onChange={(_, { value }) => {
                const v = value as string | undefined;
                if (v === undefined || v === '') setStatusFilter('');
                else setStatusFilter(Number(v) as EContractStatus);
              }}
            />
          </Form.Field>
          <Form.Input
            fluid
            label="Data do contrato - Início"
            type="date"
            value={dataInicioFilter}
            onChange={(e) => setDataInicioFilter(e.target.value)}
          />
          <Form.Input
            fluid
            label="Data do contrato - Fim"
            type="date"
            value={dataFimFilter}
            onChange={(e) => setDataFimFilter(e.target.value)}
          />
          <Form.Field>
            <label>&nbsp;</label>
            {canViewAdvancedFilters && (
              <Button
                fluid
                icon={showAdvancedFilters ? 'chevron up' : 'chevron down'}
                content={showAdvancedFilters ? 'Ocultar Filtros Avançados' : 'Mostrar Filtros Avançados'}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                style={getDefaultButtonStyle()}
                {...getButtonHoverEffects('default')}
              />
            )}
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal" stackable>
          <Form.Select
            fluid
            label="Troca de linha"
            placeholder="Todos"
            options={TRI_STATE_OPTIONS}
            value={temTrocaLinhaTriFilter}
            onChange={(e, { value }) =>
              setTemTrocaLinhaTriFilter((value as OperationalFilterTriState) ?? '')
            }
          />
          <Form.Field>
            <label>
              Linhas a suspender (pendência)
              <Popup
                trigger={<Icon name="info circle" link style={{ marginLeft: 6 }} />}
                content="Mesmo critério base do painel Linhas a suspender (antes dos filtros de urgência)."
                wide
              />
            </label>
            <Form.Select
              fluid
              placeholder="Todos"
              options={TRI_STATE_OPTIONS}
              value={temPendenciaSuspensaoTriFilter}
              onChange={(e, { value }) =>
                setTemPendenciaSuspensaoTriFilter((value as OperationalFilterTriState) ?? '')
              }
            />
          </Form.Field>
          <Form.Select
            fluid
            label="Com aparelho (catálogo)"
            placeholder="Todos"
            options={TRI_STATE_OPTIONS}
            value={temAparelhoCatalogoTriFilter}
            onChange={(e, { value }) =>
              setTemAparelhoCatalogoTriFilter((value as OperationalFilterTriState) ?? '')
            }
          />
          <Form.Field>
            <label>Cluster (localização)</label>
            <Dropdown
              placeholder="Todos os clusters"
              fluid
              multiple
              selection
              search
              clearable
              options={[...clusterFilterOptions] as import('semantic-ui-react').DropdownProps['options']}
              value={effectiveClustersFilter}
              onChange={(_, { value }) =>
                setEffectiveClustersFilter((value as string[]) ?? [])
              }
            />
          </Form.Field>
        </Form.Group>

        {canViewAdvancedFilters && showAdvancedFilters && (
          <Segment style={{ marginTop: '15px', backgroundColor: '#f8f9fa' }}>
            <Header as="h5" style={{ color: '#2c3e50', marginBottom: '15px' }}>
              <Icon name="cog" />
              Filtros Avançados
            </Header>
            <Form.Group widths="equal">
              <Form.Input
                fluid
                label="Nome do Usuário"
                placeholder="Nome do usuário responsável"
                value={userNameFilter}
                onChange={(e) => setUserNameFilter(e.target.value)}
              />
              <Form.Input
                fluid
                label="Cidade do Endereço"
                placeholder="Cidade de entrega"
                value={enderecoCidadeFilter}
                onChange={(e) => setEnderecoCidadeFilter(e.target.value)}
              />
              <Form.Select
                fluid
                label="Estado do Endereço"
                placeholder="Todos os estados"
                options={ESTADOS_OPTIONS}
                value={enderecoEstadoFilter}
                onChange={(e, { value }) => setEnderecoEstadoFilter((value as string) ?? '')}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                fluid
                label="Nome do Responsável pela Auditoria"
                placeholder="Nome do responsável pela auditoria"
                value={administratorNomeFilter}
                onChange={(e) => setAdministratorNomeFilter(e.target.value)}
              />
              <Form.Input
                fluid
                label="Busca Geral"
                placeholder="Busca em todos os campos"
                icon="search"
                value={searchTermFilter}
                onChange={(e) => setSearchTermFilter(e.target.value)}
              />
              <Form.Field>
                <label>&nbsp;</label>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <Button
                    icon="search"
                    content="Aplicar Filtros"
                    onClick={applyFilters}
                    loading={contractsLoading}
                    disabled={contractsLoading}
                    style={getDefaultButtonStyle()}
                    {...getButtonHoverEffects('default')}
                  />
                  <Button
                    icon="trash"
                    content="Limpar"
                    onClick={clearFilters}
                    disabled={contractsLoading}
                    style={getDefaultButtonStyle()}
                    {...getButtonHoverEffects('default')}
                  />
                </div>
              </Form.Field>
            </Form.Group>
          </Segment>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <Button
            primary
            icon="search"
            content="Aplicar Filtros"
            onClick={applyFilters}
            loading={contractsLoading}
            disabled={contractsLoading}
            style={getDefaultButtonStyle()}
            {...getButtonHoverEffects('default')}
          />
          <Button
            secondary
            icon="trash"
            content="Limpar Filtros"
            onClick={clearFilters}
            disabled={contractsLoading}
            style={getDefaultButtonStyle()}
            {...getButtonHoverEffects('default')}
          />
        </div>
      </Form>
    </Segment>
  );
};

export default ContractFilters;
